/**
 * THE SALE CARD UNDER THE PRICE.
 *
 * dk-store's own product page puts a card under the price whenever its
 * automatic coupon applies: a dot and "15% הנחה", which promotion it is, the
 * old price struck and the new one large. This is that card's structure and
 * copy (their markup: `.dkpm-promo-card`), in this shop's material — the
 * product page's own hairline-on-white surface and its type scale, never their
 * hex — and WITHOUT their four-feature grid: the shop already has a features
 * section and product policies, and a second copy of them would be two answers
 * on one page.
 *
 * WHAT IT IS ALLOWED TO SAY. Everything on it is derived from the one display
 * rule (`campaignLinePricing`, the rule every card uses), which mirrors what
 * the checkout charges, so the card appears only when a campaign discount
 * really applies to the chosen variant, and:
 *   - it names EVERY campaign in a stack, because the shopper gets all of them;
 *   - "על כל האתר" only when every applied campaign is site-wide — a stack that
 *     includes a per-product discount is not a site-wide number;
 *   - "לזמן מוגבל" only when the owner set an end date. A promotion that runs
 *     until the owner stops it is "פעיל עכשיו"; urgency nobody set is a lie.
 *
 * A compare-at "sale" alone draws no card. That is a price the owner typed, not
 * an automatic discount at the checkout, and the price row already strikes it.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { currencyDecimals, formatMoney } from '../../utils/currency';
import styles from './productSaleCard.module.css';

/* Interpolated values are passed to `t` as marked slots and swapped back in as
 * elements, so an amount can be bidi-isolated and a name highlighted without a
 * translation having to carry markup. U+2063 (INVISIBLE SEPARATOR) never occurs
 * in a sentence. */
const MARK = '\u2063';
const slot = (name) => `${MARK}${name}${MARK}`;
const fill = (text, nodes) => String(text)
  .split(MARK)
  .map((part, i) => (i % 2 === 1
    ? <React.Fragment key={`${part}-${i}`}>{nodes[part] ?? part}</React.Fragment>
    : part));

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * What the card states, or null when there is nothing to state. Pure, so the
 * wording rules above are testable without a render.
 */
/**
 * A campaign that only ever gives free delivery (a 0% value, with or without a
 * threshold) is never a discount, and one whose share of THIS line came to
 * nothing did not take part — neither is named (UX-SHOP-8: "C8 משלוח חינם מעל
 * ₪200" was listed in the title as part of a price reduction).
 */
const tookSomethingOff = (a) => !!a?.campaign
  && Number(a.campaign.discountValue) > 0
  && !(a.discountAmount != null && !(Number(a.discountAmount) > 0));

export function saleCardModel(pricing) {
  const campaigns = (pricing?.applied || []).filter(tookSomethingOff).map((a) => a.campaign);
  const oldPrice = Number(pricing?.listPrice) || 0;
  const discount = Number(pricing?.discount) || 0;
  if (campaigns.length === 0 || !(discount > 0) || !(oldPrice > 0)) return null;
  const allPercent = campaigns.every((c) => c.discountType === 'PERCENT');
  const pct = allPercent
    ? round2(Math.min(100, campaigns.reduce((sum, c) => sum + Math.min(100, Number(c.discountValue) || 0), 0)))
    : null;
  return {
    pct,
    discount: round2(discount),
    oldPrice,
    newPrice: round2(Math.max(0, oldPrice - discount)),
    sitewide: campaigns.every((c) => c.targetType === 'ALL'),
    limited: campaigns.some((c) => !!c.bookingDateEnd),
    names: campaigns.map((c) => c.name).filter(Boolean),
  };
}

export default function ProductSaleCard({ pricing, currency, soldOut = false }) {
  const { t, i18n } = useTranslation();
  const model = saleCardModel(pricing);
  if (!model) return null;

  const language = String(i18n?.language || 'en').split('-')[0];
  const rtl = language === 'he' || language === 'ar';
  const formatAmount = (amount) => formatMoney(amount, currency, {
    decimals: Number.isInteger(round2(amount)) ? 0 : currencyDecimals(currency),
    position: rtl ? 'suffix' : 'prefix',
    locale: language,
  });

  const discountText = model.pct != null ? `${model.pct}%` : formatAmount(model.discount);
  /* Each name its own highlighted run, joined by breakable " + " text: one
   * `nowrap` run of every name in a stack ran 254px off a phone's card. */
  const nameRuns = model.names.map((name, i) => (
    <React.Fragment key={`${name}-${i}`}>
      {i > 0 ? ' + ' : null}
      <span className={styles.highlight} dir="auto">{name}</span>
    </React.Fragment>
  ));
  const nodes = {
    D: <span className={styles.amount}>{discountText}</span>,
    N: <>{nameRuns}</>,
  };
  const say = (key, defaultValue) => fill(t(key, { discount: slot('D'), name: slot('N'), defaultValue }), nodes);
  /* EACH FIGURE WHERE THE OWNER KEPT IT, AND NOWHERE ELSE (UX-SHOP-8).
   *
   * dk-store's card says "15%" five times: top line, caption, subtitle, status
   * and a price pill. The owner asked for their card "exactly like theirs",
   * kept four of its sentences word for word — "15% הנחה", "15% הנחה אוטומטית
   * על כל האתר", "מבצע SUMMER15 פעיל עכשיו" and "בקופה תראו…" — and let the
   * repetition go. So:
   *   - the SUBTITLE says "the discount" without the figure, in both kinds;
   *   - the PRICE PILL is gone. Its only content was the figure again, beside a
   *     label ("המחיר לאחר ההנחה") that already says what the big number is;
   *   - a money discount ("87.50 ₪ הנחה") is stated once, in the top line: an
   *     amount repeated reads as noise, and the caption and status around it
   *     say "the discount" instead. */
  const money = model.pct == null;
  const sayPlain = (key, defaultValue) => t(key, { defaultValue });

  return (
    <section
      className={styles.card}
      dir={rtl ? 'rtl' : 'ltr'}
      aria-label={t('productDetail.saleAria', { defaultValue: 'Sale on now' })}
      data-testid="product-sale-card"
    >
      <div className={styles.topline}>
        <span className={styles.campaign} data-testid="product-sale-card-topline">
          <span className={styles.dot} aria-hidden="true" />
          <span>{say('productDetail.saleTopline', '{{discount}} off')}</span>
        </span>
        <span className={styles.caption} data-testid="product-sale-card-caption">
          {money && (model.sitewide
            ? sayPlain('productDetail.saleCaptionAllPlain', 'Automatic discount, sitewide')
            : sayPlain('productDetail.saleCaptionProductPlain', 'Automatic discount on this product'))}
          {!money && (model.sitewide
            ? say('productDetail.saleCaptionAll', '{{discount}} off automatically, sitewide')
            : say('productDetail.saleCaptionProduct', '{{discount}} off automatically on this product'))}
        </span>
      </div>

      <div className={styles.copy}>
        <h2 className={styles.title}>
          {model.limited
            ? say('productDetail.saleTitleLimited', 'The {{name}} sale is on for a limited time')
            : say('productDetail.saleTitleNow', 'The {{name}} sale is on now')}
        </h2>
        {/* Sold out: the sale is still true and still stated, but nothing
          * here may urge an order nobody can place (UX-SHOP-21). */}
        {!soldOut && (
          <p className={styles.subtitle} data-testid="product-sale-card-subtitle">
            {model.sitewide
              ? sayPlain('productDetail.saleSubtitleAllPlain', 'Complete your order now and enjoy the discount on everything in the shop.')
              : sayPlain('productDetail.saleSubtitleProductPlain', 'Complete your order now and enjoy the discount on this product.')}
          </p>
        )}
      </div>

      {!soldOut && (
        <p className={styles.status} aria-live="polite" data-testid="product-sale-card-status">
          {money
            ? sayPlain('productDetail.saleStatusPlain', 'At checkout the discount is applied to this product automatically.')
            : say('productDetail.saleStatus', 'At checkout you will see {{discount}} off this product, applied automatically.')}
        </p>
      )}

      <div className={styles.priceBox}>
        <div className={styles.priceMain}>
          <del className={styles.priceOld} data-testid="product-sale-card-old">
            <span className={styles.amount}>{formatAmount(model.oldPrice)}</span>
          </del>
          <span className={styles.priceNew} data-testid="product-sale-card-new">
            <span className={styles.amount}>{formatAmount(model.newPrice)}</span>
          </span>
        </div>
        <span className={styles.priceLabel}>
          {t('productDetail.salePriceLabel', { defaultValue: 'Price after discount' })}
        </span>
      </div>
    </section>
  );
}
